import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ChevronDown,
  Search,
  Filter,
  RefreshCw,
  UserPlus,
  Sliders,
  Users,
  Phone,
} from "lucide-react";
import { TrendUp, Add, ImportCurve } from "iconsax-reactjs";
import apiService from "../../services/api";
import LushaImportModal from "../../components/LushaImportModal";
import ImportLeadsModal from "../../components/ImportLeadsModal";
import CreateLeadModal from "../../components/CreateLeadModal";
import { useNavigate } from "react-router-dom";
import LeadDetailsModal from "../../components/LeadDetailsModal";
import BulkAssignModal from "../../components/BulkAssignModal";
import AdvancedLeadFilterModal from "../../components/AdvancedLeadFilterModal";
import AutoAssignLeadsModal from "../../components/AutoAssignLeadsModal";
import AutoAssignUnqualifiedModal from "../../components/AutoAssignUnqualifiedModal";
import CallbackDailyFilterModal from "../../components/CallbackDailyFilterModal";
import AutoAssignCallbacksModal from "../../components/AutoAssignCallbacksModal";
import LeadSearchInput from "../../components/LeadSearchInput";
import { useRealtimeSync } from "../../hooks/useRealtimeSync";
import { useTheme } from "../../contexts/ThemeContext";

const Leads = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({
    totalLeads: 0,
    newLeadsToday: 0,
    conversionRate: 0,
  });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Basic filters
  const [basicFilters, setBasicFilters] = useState({
    status: "",
    source: "",
    assignedTo: "",
    search: "",
  });

  // Advanced filters
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [activePresetId, setActivePresetId] = useState(null);
  const [activePresetName, setActivePresetName] = useState(null);

  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [showAutoAssignModal, setShowAutoAssignModal] = useState(false);
  const [showLushaModal, setShowLushaModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [users, setUsers] = useState([]);
  const [showCallbackFilterModal, setShowCallbackFilterModal] = useState(false);
  const [showAutoAssignCallbacksModal, setShowAutoAssignCallbacksModal] =
    useState(false);
  const [showAutoAssignUnqualifiedModal, setShowAutoAssignUnqualifiedModal] =
    useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const actionsDropdownRef = useRef(null);
  const handleRealtimeUpdate = useCallback((data) => {
    console.log("📡 Received real-time lead update:", data);

    if (data.type === "lead_updated") {
      const { action, data: leadData } = data;

      setLeads((prevLeads) => {
        if (action === "created") {
          return [leadData, ...prevLeads];
        } else if (
          action === "updated" ||
          action === "assigned" ||
          action === "qualified" ||
          action === "converted"
        ) {
          return prevLeads.map((lead) =>
            lead.id === leadData.id ? { ...lead, ...leadData } : lead,
          );
        } else if (action === "deleted") {
          return prevLeads.filter((lead) => lead.id !== leadData.id);
        } else if (action === "imported") {
          fetchLeads();
          fetchStats();
          return prevLeads;
        }
        return prevLeads;
      });

      if (action !== "imported") {
        fetchStats();
      }

      if (action === "deleted") {
        setSelectedLeads((prev) => prev.filter((id) => id !== leadData.id));
      }
    }
  }, []);

  const { isConnected } = useRealtimeSync({
    events: ["lead_updated"],
    onUpdate: handleRealtimeUpdate,
    enabled: true,
  });

  useEffect(() => {
    apiService.warmupLeadCache().catch(console.error);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        actionsDropdownRef.current &&
        !actionsDropdownRef.current.contains(e.target)
      ) {
        setShowActionsDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchLeads();
    fetchTeamMembers();
  }, [pagination.page, basicFilters, advancedFilters]);

  const fetchTeamMembers = async () => {
    try {
      const teamsResponse = await apiService.getUserTeams();
      const teams = teamsResponse.teams || [];

      if (teams.length === 0) {
        console.warn("User is not a member of any team");
        return;
      }

      const allMembers = new Map();

      for (const team of teams) {
        try {
          const membersResponse = await apiService.getTeamMembers(team.id);
          const members = membersResponse.members || [];

          members.forEach((member) => {
            if (member.user && member.status === "active") {
              allMembers.set(member.user.id, {
                id: member.user.id,
                name: member.user.name,
                email: member.user.email,
                profileImageUrl: member.user.profileImageUrl,
                role: member.user.role,
              });
            }
          });
        } catch (err) {
          console.error(`Error fetching members for team ${team.id}:`, err);
        }
      }

      setUsers(Array.from(allMembers.values()));
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);

      let response;

      if (advancedFilters) {
        response = await apiService.getLeadsAdvanced({
          page: pagination.page,
          limit: pagination.limit,
          advancedFilters: JSON.stringify(advancedFilters),
          filterPresetId: activePresetId,
        });
      } else {
        response = await apiService.getLeads({
          page: pagination.page,
          limit: pagination.limit,
          ...basicFilters,
        });
      }

      setLeads(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiService.getLeadStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleApplyAdvancedFilters = async (filterConfig, presetId) => {
    setAdvancedFilters(filterConfig);
    setActivePresetId(presetId);
    setPagination({ ...pagination, page: 1 });

    if (presetId) {
      try {
        const response = await apiService.getFilterPreset(presetId);
        setActivePresetName(response.data.name);
      } catch (error) {
        console.error("Error fetching preset name:", error);
      }
    } else {
      setActivePresetName(null);
    }
  };

  const handleClearAdvancedFilters = () => {
    setAdvancedFilters(null);
    setActivePresetId(null);
    setActivePresetName(null);
    setPagination({ ...pagination, page: 1 });
  };

  const handleImportSuccess = () => {
    fetchLeads();
    fetchStats();
    setShowLushaModal(false);
  };

  const handleCreateSuccess = () => {
    fetchLeads();
    fetchStats();
    setShowCreateModal(false);
  };

  const handleUpdateLead = async (leadId, data) => {
    try {
      await apiService.updateLead(leadId, data);
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error("Error updating lead:", error);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (window.confirm("Are you sure you want to delete this lead?")) {
      try {
        await apiService.deleteLead(leadId);
        fetchLeads();
        fetchStats();
        setSelectedLead(null);
      } catch (error) {
        console.error("Error deleting lead:", error);
      }
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads((prev) => {
      if (prev.includes(leadId)) {
        return prev.filter((id) => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map((lead) => lead.id));
    }
  };

  const handleBulkAssignSuccess = () => {
    setShowBulkAssign(false);
    setSelectedLeads([]);
    fetchLeads();
  };

  const getStatusColor = (status) => {
    if (isDarkMode) {
      const colors = {
        new: "text-blue-400 bg-[#0C0E12]",
        nurture: "text-orange-400 bg-[#0C0E12]",
        qualified: "text-green-400 bg-[#0C0E12]",
        unqualified: "text-gray-400 bg-[#0C0E12]",
        converted: "text-purple-400 bg-[#0C0E12]",
        lost: "text-red-400 bg-[#0C0E12]",
      };
      return colors[status] || "text-gray-400 bg-[#0C0E12]";
    } else {
      const colors = {
        new: "text-blue-600 bg-white",
        nurture: "text-orange-600 bg-white",
        qualified: "text-green-600 bg-white",
        unqualified: "text-gray-500 bg-white",
        converted: "text-purple-600 bg-white",
        lost: "text-red-600 bg-white",
      };
      return colors[status] || "text-gray-500 bg-white";
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      unqualified: "Unqualified",
      lead_pot: "Lead Pot",
      new: "New",
      nurture: "Nurture",
      dead: "Dead",
      converted: "Converted",
    };
    return labels[status] || "Unknown";
  };

  const StatCard = ({ title, value, change }) => (
    <div
      className={`rounded-xl p-6 border transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700/50 hover:border-gray-600/50" : "bg-white border-gray-200 hover:border-gray-300"}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div
          className={`text-[14px] xl:text-[14px] font-medium ${isDarkMode ? "text-[#94979C]" : "text-gray-500"}`}
        >
          {title}
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-end justify-between">
          <div
            className={`text-[28px] xl:text-[20px] font-bold ${isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"}`}
          >
            {value}
          </div>
          {change !== undefined && (
            <div
              className={`flex items-center text-sm xl:text-[10px] font-medium ${isDarkMode ? "text-white" : "text-gray-700"}`}
            >
              <TrendUp className="mr-1 text-green-400 xl:w-5 xl:h-5" />
              {change}%
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const LeadRow = ({ lead }) => {
    const initials = lead.fullName
      ? lead.fullName
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2)
      : "??";

    const S3_BASE_URL = "https://fibre-crm.s3.eu-west-2.amazonaws.com/";

    const assignedUser =
      users.find((u) => u.id === lead.assignedTo) || lead.assignedUser || null;

    const getProfileImageUrl = (url) => {
      if (!url) return null;
      if (url.startsWith("http://") || url.startsWith("https://")) return url;
      return `${S3_BASE_URL}${url}`;
    };

    return (
      <tr
        className={`border-b transition-colors ${isDarkMode ? "border-gray-700 hover:bg-gray-800/50" : "border-gray-200 hover:bg-gray-50"}`}
      >
        <td className="py-4 px-4">
          <input
            type="checkbox"
            checked={selectedLeads.includes(lead.id)}
            onChange={() => handleSelectLead(lead.id)}
            className={`w-4 h-4 rounded text-blue-600 focus:ring-blue-500 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-300 bg-white"}`}
          />
        </td>
        <td className="py-4 px-4">
          <button
            onClick={() => window.open(`/leads/${lead.id}`, "_blank")}
            className={`font-medium hover:text-blue-400 transition-colors ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {lead.companyName || "N/A"}
          </button>
        </td>
        <td className="py-4 px-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
            >
              {initials}
            </div>
            <div>
              <div
                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {lead.fullName || "N/A"}
              </div>
              <div
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {lead.email || "N/A"}
              </div>
            </div>
          </div>
        </td>
        <td className="py-4 px-4">
          {lead.phone ? (
            <a
              href={`tel:${lead.phone}`}
              className="text-blue-400 hover:text-blue-300 transition-colors hover:underline"
            >
              {lead.phone}
            </a>
          ) : (
            <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              N/A
            </span>
          )}
        </td>
        <td className="py-4 px-4">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full border text-xs font-medium ${isDarkMode ? "border-[#373A41]" : "border-gray-300"} ${getStatusColor(lead.leadStatus)}`}
          >
            <div className="w-2 h-2 rounded-full bg-current mr-2"></div>
            {getStatusLabel(lead.leadStatus)}
          </span>
        </td>
        <td className="py-4 px-4">
          {assignedUser ? (
            <div className="flex items-center gap-2">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
              >
                {assignedUser.profileImageUrl ? (
                  <img
                    src={getProfileImageUrl(assignedUser.profileImageUrl)}
                    alt={assignedUser.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  assignedUser.name?.charAt(0).toUpperCase()
                )}
              </div>
              <span
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {assignedUser.name}
              </span>
            </div>
          ) : (
            <span
              className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              Unassigned
            </span>
          )}
        </td>
        <td className="py-4 px-4 text-right">
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => setSelectedLead(lead)}
              className={`transition-colors p-1 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-700"}`}
            >
              <Search size={16} />
            </button>
            <button
              onClick={() => handleDeleteLead(lead.id)}
              className={`transition-colors p-1 ${isDarkMode ? "text-gray-400 hover:text-red-400" : "text-gray-400 hover:text-red-500"}`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path d="M6 2h4v1H6V2zm7 1h-2V2a1 1 0 00-1-1H6a1 1 0 00-1 1v1H3a1 1 0 000 2h1v8a2 2 0 002 2h4a2 2 0 002-2V5h1a1 1 0 100-2zM6 13V6h1v7H6zm3 0V6h1v7H9z" />
              </svg>
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1
              className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Leads
            </h1>
            <p
              className={`text-sm sm:text-base leading-relaxed ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Manage all your leads and opportunities in one place.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 sm:gap-3 shrink-0">
            <button
              onClick={() => setShowImportModal(true)}
              className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg transition-colors text-sm sm:text-base font-medium min-w-0 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
            >
              <ImportCurve size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Import CSV/Excel</span>
            </button>

            <button
              onClick={fetchLeads}
              className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg transition-colors text-sm sm:text-base font-medium min-w-0 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
            >
              <RefreshCw size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Refresh</span>
            </button>

            <div className="relative" ref={actionsDropdownRef}>
              <button
                onClick={() => setShowActionsDropdown((prev) => !prev)}
                className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg transition-colors text-sm sm:text-base font-medium min-w-0 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
              >
                <Sliders size={16} className="shrink-0" />
                <span className="whitespace-nowrap">Actions</span>
                <ChevronDown
                  size={14}
                  className={`shrink-0 transition-transform duration-200 ${showActionsDropdown ? "rotate-180" : ""}`}
                />
              </button>

              {showActionsDropdown && (
                <>
                  {/* Mobile: full-screen overlay + bottom sheet */}
                  <div
                    className="sm:hidden fixed inset-0 z-40 bg-black/40"
                    onClick={() => setShowActionsDropdown(false)}
                  />
                  <div
                    className={`sm:hidden fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl border-t shadow-2xl ${isDarkMode ? "bg-[#0C0E12] border-[#22262F]" : "bg-white border-gray-200"}`}
                  >
                    <div className="flex justify-center pt-3 pb-1">
                      <div
                        className={`w-10 h-1 rounded-full ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`}
                      />
                    </div>
                    <p
                      className={`px-5 py-3 text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      Actions
                    </p>
                    <div className="pb-8">
                      <button
                        onClick={() => {
                          setShowAutoAssignModal(true);
                          setShowActionsDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 px-5 py-4 text-base transition-colors ${isDarkMode ? "text-gray-300 hover:bg-[#22262F]" : "text-gray-700 hover:bg-gray-50"}`}
                      >
                        <Users size={18} className="shrink-0 text-blue-400" />
                        Auto-Assign
                      </button>
                      <button
                        onClick={() => {
                          setShowAutoAssignUnqualifiedModal(true);
                          setShowActionsDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 px-5 py-4 text-base transition-colors ${isDarkMode ? "text-gray-300 hover:bg-[#22262F]" : "text-gray-700 hover:bg-gray-50"}`}
                      >
                        <Users size={18} className="shrink-0 text-orange-400" />
                        Auto-Assign Unqualified
                      </button>
                      <div
                        className={`mx-5 my-1 border-t ${isDarkMode ? "border-[#22262F]" : "border-gray-100"}`}
                      />
                      <button
                        onClick={() => {
                          setShowCallbackFilterModal(true);
                          setShowActionsDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 px-5 py-4 text-base transition-colors ${isDarkMode ? "text-gray-300 hover:bg-[#22262F]" : "text-gray-700 hover:bg-gray-50"}`}
                      >
                        <Phone size={18} className="shrink-0 text-blue-400" />
                        Set Callback Filter
                      </button>
                      <button
                        onClick={() => {
                          setShowAutoAssignCallbacksModal(true);
                          setShowActionsDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 px-5 py-4 text-base transition-colors ${isDarkMode ? "text-gray-300 hover:bg-[#22262F]" : "text-gray-700 hover:bg-gray-50"}`}
                      >
                        <Users size={18} className="shrink-0 text-blue-400" />
                        Auto-Assign Callbacks
                      </button>
                      <div
                        className={`mx-5 my-1 border-t ${isDarkMode ? "border-[#22262F]" : "border-gray-100"}`}
                      />
                      <button
                        onClick={() => {
                          setShowAdvancedFilter(true);
                          setShowActionsDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 px-5 py-4 text-base transition-colors ${isDarkMode ? "text-gray-300 hover:bg-[#22262F]" : "text-gray-700 hover:bg-gray-50"}`}
                      >
                        <Sliders size={18} className="shrink-0 text-blue-400" />
                        Advanced Filters
                      </button>
                    </div>
                  </div>

                  {/* Desktop: standard dropdown */}
                  <div
                    className={`hidden sm:block absolute right-0 top-full mt-2 w-56 rounded-xl border shadow-2xl z-50 overflow-hidden ${isDarkMode ? "bg-[#0C0E12] border-[#22262F]" : "bg-white border-gray-200"}`}
                  >
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowAutoAssignModal(true);
                          setShowActionsDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDarkMode ? "text-gray-300 hover:bg-[#22262F]" : "text-gray-700 hover:bg-gray-50"}`}
                      >
                        <Users size={15} className="shrink-0 text-blue-400" />
                        Auto-Assign
                      </button>
                      <button
                        onClick={() => {
                          setShowAutoAssignUnqualifiedModal(true);
                          setShowActionsDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDarkMode ? "text-gray-300 hover:bg-[#22262F]" : "text-gray-700 hover:bg-gray-50"}`}
                      >
                        <Users size={15} className="shrink-0 text-orange-400" />
                        Auto-Assign Unqualified
                      </button>
                      <div
                        className={`my-1 border-t ${isDarkMode ? "border-[#22262F]" : "border-gray-100"}`}
                      />
                      <button
                        onClick={() => {
                          setShowCallbackFilterModal(true);
                          setShowActionsDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDarkMode ? "text-gray-300 hover:bg-[#22262F]" : "text-gray-700 hover:bg-gray-50"}`}
                      >
                        <Phone size={15} className="shrink-0 text-blue-400" />
                        Set Callback Filter
                      </button>
                      <button
                        onClick={() => {
                          setShowAutoAssignCallbacksModal(true);
                          setShowActionsDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDarkMode ? "text-gray-300 hover:bg-[#22262F]" : "text-gray-700 hover:bg-gray-50"}`}
                      >
                        <Users size={15} className="shrink-0 text-blue-400" />
                        Auto-Assign Callbacks
                      </button>
                      <div
                        className={`my-1 border-t ${isDarkMode ? "border-[#22262F]" : "border-gray-100"}`}
                      />
                      <button
                        onClick={() => {
                          setShowAdvancedFilter(true);
                          setShowActionsDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDarkMode ? "text-gray-300 hover:bg-[#22262F]" : "text-gray-700 hover:bg-gray-50"}`}
                      >
                        <Sliders size={15} className="shrink-0 text-blue-400" />
                        Advanced Filters
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                  0px 36px 32px -16px #427BCA36,
                  0px 8px 16px 0px #2F578F1F,
                  0px 2px 4px -8px #427BCA14,
                  0px 8px 24px -16px #FFFFFF3D inset,
                  0px -24px 32px 0px #FFFFFF38 inset,
                  0px 0px 12px 0px #FFFFFF inset
                `,
              }}
            >
              <Add size={16} className="shrink-0" />
              <span className="whitespace-nowrap">New Lead</span>
            </button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedLeads.length > 0 && (
          <div className="mb-4 bg-blue-600/10 border border-blue-600/30 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <span className="text-blue-400 font-medium">
              {selectedLeads.length} lead{selectedLeads.length !== 1 ? "s" : ""}{" "}
              selected
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowBulkAssign(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
              >
                <UserPlus size={16} />
                Assign to User
              </button>
              <button
                onClick={() => setSelectedLeads([])}
                className={`px-4 py-2 rounded-lg transition-colors text-sm ${isDarkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {/* Active Advanced Filter Badge */}
        {advancedFilters && (
          <div className="mb-4 bg-purple-600/10 border border-purple-600/30 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Sliders size={20} className="text-purple-400 shrink-0" />
              <div>
                <div className="text-purple-400 font-medium">
                  {activePresetName
                    ? `Filter: ${activePresetName}`
                    : "Advanced Filter Active"}
                </div>
                <div
                  className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {advancedFilters.conditions.length} condition
                  {advancedFilters.conditions.length !== 1 ? "s" : ""} applied
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowAdvancedFilter(true)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
              >
                Edit Filter
              </button>
              <button
                onClick={handleClearAdvancedFilters}
                className={`px-4 py-2 rounded-lg transition-colors text-sm ${isDarkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
              >
                Clear Filter
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <LeadSearchInput
              value={basicFilters.search}
              onChange={(value) =>
                setBasicFilters({ ...basicFilters, search: value })
              }
              placeholder="Search leads..."
            />
          </div>
        </div>

        {/* Table */}
        <div
          className={`rounded-lg border mb-8 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"}`}
        >
          <div
            className={`flex items-start sm:items-center justify-between p-4 sm:p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div>
              <h2
                className={`text-lg sm:text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Leads{" "}
                <span
                  className={`text-sm ml-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {pagination.total} leads
                </span>
              </h2>
              <p
                className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Keep track of your leads.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className={isDarkMode ? "bg-[#13161B]" : "bg-gray-50"}>
                <tr
                  className={`border-b ${isDarkMode ? "border-[#22262F]" : "border-gray-200"}`}
                >
                  <th className="text-left py-4 px-4">
                    <input
                      type="checkbox"
                      checked={
                        selectedLeads.length === leads.length &&
                        leads.length > 0
                      }
                      onChange={handleSelectAll}
                      className={`w-4 h-4 rounded text-blue-600 focus:ring-blue-500 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-300 bg-white"}`}
                    />
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Company
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Contact
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Phone Number
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Status
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Assigned To
                  </th>
                  <th
                    className={`text-right py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="7"
                      className={`py-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Loading leads...
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className={`py-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {advancedFilters
                        ? "No leads match your filters. Try adjusting your criteria."
                        : "No leads found. Import from Lusha or create manually."}
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => <LeadRow key={lead.id} lead={lead} />)
                )}
              </tbody>
            </table>
          </div>

          <div
            className={`flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t gap-4 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page - 1 })
                }
                disabled={pagination.page === 1}
                className={`px-3 py-1 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page + 1 })
                }
                disabled={pagination.page >= pagination.totalPages}
                className={`px-3 py-1 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
              >
                Next
              </button>
            </div>
            <span
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Page {pagination.page} of {pagination.totalPages}
            </span>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showLushaModal && (
        <LushaImportModal
          onClose={() => setShowLushaModal(false)}
          onSuccess={handleImportSuccess}
        />
      )}

      {showCreateModal && (
        <CreateLeadModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {selectedLead && (
        <LeadDetailsModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={handleUpdateLead}
          onDelete={handleDeleteLead}
        />
      )}

      {showImportModal && (
        <ImportLeadsModal
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            fetchLeads();
            fetchStats();
          }}
        />
      )}

      {showBulkAssign && (
        <BulkAssignModal
          leadIds={selectedLeads}
          users={users}
          onClose={() => setShowBulkAssign(false)}
          onSuccess={handleBulkAssignSuccess}
        />
      )}

      {showAdvancedFilter && (
        <AdvancedLeadFilterModal
          isOpen={showAdvancedFilter}
          onClose={() => setShowAdvancedFilter(false)}
          onApplyFilters={handleApplyAdvancedFilters}
        />
      )}

      {showAutoAssignModal && (
        <AutoAssignLeadsModal
          isOpen={showAutoAssignModal}
          onClose={() => setShowAutoAssignModal(false)}
          onSuccess={() => {
            setShowAutoAssignModal(false);
            fetchLeads();
            fetchStats();
          }}
        />
      )}
      {showAutoAssignUnqualifiedModal && (
        <AutoAssignUnqualifiedModal
          isOpen={showAutoAssignUnqualifiedModal}
          onClose={() => setShowAutoAssignUnqualifiedModal(false)}
          onSuccess={() => {
            setShowAutoAssignUnqualifiedModal(false);
            fetchLeads();
            fetchStats();
          }}
        />
      )}

      <CallbackDailyFilterModal
        isOpen={showCallbackFilterModal}
        onClose={() => setShowCallbackFilterModal(false)}
        onSaved={() => setShowCallbackFilterModal(false)}
      />

      <AutoAssignCallbacksModal
        isOpen={showAutoAssignCallbacksModal}
        onClose={() => setShowAutoAssignCallbacksModal(false)}
        onSuccess={() => {
          fetchLeads();
          fetchStats();
        }}
        // teamId={/* your teamId variable, or omit for company-wide */}
      />
    </div>
  );
};

export default Leads;
